/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useState } from "react";
import { useSnmpAgentData } from "./hooks";
import { selectedSnmpAgentSelector } from "../../../../store/api/admin/selectors";
import { useSelector } from "react-redux";
import { handleVersionDataChecked, handleVersionDataUnchecked } from "../../helpers";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { constStr } from "../../../../globals/constants";
import { confirmDialogSelector, errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { isValidPassword } from "../../../../utils/validations";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { PasswordField } from "../../../Shared/Radix/Inputs/PasswordField";
import { Label } from "../../../ui/label";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { RadixSelectControl } from "../../../ui/radix-select";
import { getProtocolList } from "../../../../pages/Administrations/helpers";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { store } from "../../../../store";
import { ShadcnHeading } from "../../../ui/shadcn-heading";
import { SxProps } from "@mui/material";

interface IStateKey {
  [key: string]: any;
}

const SnmpAgent: React.FC = () => {
  const snmpAgentDetails = useSelector(selectedSnmpAgentSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const storeErrMsg = useSelector(errorMsgSelector);
  const dispatch = useStoreDispatch();
  const {
    AuthenticationOptions,
    PrivacyOptions,
    isAuthChecked,
    setIsAuthChecked,
    isPrivChecked,
    setIsPrivChecked,
    snmpAgentData,
    setSnmpAgentData,
    isLoading,
    authConfirmPassword,
    setAuthConfirmPassword,
    privConfirmPassword,
    setPrivConfirmPassword,
    submitHandler,
    handleResetSnmpAgent,
    onBlurHandler,
    agentEnable,
    enableV2,
    enableV3,
    setAgentEnable,
    setEnableV2,
    setEnableV3,
    handleSelectVersion,
    handleMsg,
    handleUpdateSnmpVersion,
    authPassword,
    setAuthPassword,
    privPassword,
    setPrivPassword,
  } = useSnmpAgentData();

  const onFieldChange = (name: string, value: string) => {
    setSnmpAgentData({ ...snmpAgentData, [name]: value });
    dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, [name]: "" }));
  };

  const onPassChange = (name: string, value: string) => {
    const existing = store.getState().helpers.errorMsg;
    if (name === "authPassword") {
      setAuthPassword(value);
      if (value) {
        const isValidPass = isValidPassword(value);
        dispatch(setErrorMsg({
          ...existing,
          authPassword: isValidPass ? "" : "Password is invalid. It must meet the specified criteria!",
        }));
      } else {
        dispatch(setErrorMsg({ ...existing, authPassword: "" }));
      }
    } else if (name === "privPassword") {
      setPrivPassword(value);
      if (value) {
        const isValidPass = isValidPassword(value);
        dispatch(setErrorMsg({
          ...existing,
          privPassword: isValidPass ? "" : "Password is invalid. It must meet the specified criteria!",
        }));
      } else {
        dispatch(setErrorMsg({ ...existing, privPassword: "" }));
      }
    } else if (name === "authConfirmPassword") {
      setAuthConfirmPassword(value);
      dispatch(setErrorMsg({
        ...existing,
        authConfirmPassword: value !== authPassword ? "Passwords do not match" : "",
      }));
    } else if (name === "privConfirmPassword") {
      setPrivConfirmPassword(value);
      dispatch(setErrorMsg({
        ...existing,
        privConfirmPassword: value !== privPassword ? "Passwords do not match" : "",
      }));
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSnmpAgentData({ ...snmpAgentData, [name]: value });
    dispatch(setErrorMsg({ ...store.getState().helpers.errorMsg, [name]: "" }));
  };

  const handleSnmpAgentEnable = (checked: boolean) => {
    if (!checked) {
      const newData: any = handleVersionDataUnchecked("snmpAgentEnable", snmpAgentData, snmpAgentDetails);
      setSnmpAgentData(newData);
      setEnableV2(false);
      setEnableV3(false);
      dispatch(setErrorMsg({}));
    } else {
      const newData: any = handleVersionDataChecked("snmpAgentEnable", snmpAgentData, snmpAgentDetails);
      setSnmpAgentData(newData);
      handleUpdateSnmpVersion(snmpAgentData?.agentVersion);
    }
    setAgentEnable(checked);
  };

  const handleVersionCheckbox = (name: string, checked: boolean) => {
    if (name === "enableV2") setEnableV2(checked);
    if (name === "enableV3") setEnableV3(checked);
    if (checked) {
      const newData: any = handleVersionDataChecked(name, snmpAgentData, snmpAgentDetails);
      setSnmpAgentData(newData);
    } else {
      const newData: any = handleVersionDataUnchecked(name, snmpAgentData, snmpAgentDetails);
      setSnmpAgentData(newData);
      const existing = store.getState().helpers.errorMsg;
      dispatch(setErrorMsg({ ...existing, [name]: "", engineId: "" }));
    }
  };

  const isSnmpAgentDataUpdated = (initialState: IStateKey, currentState: IStateKey) => {
    const initialKeys: any = Object.keys(initialState);
    let isValidTrap: boolean = true;
    for (const key of initialKeys) {
      if (key === "authPassword" || key === "privPassword") {
        continue;
      }
      if (initialState[key] !== currentState[key]) {
        isValidTrap = false;
      }
    }
    if (authPassword !== snmpAgentData?.authPassword || privPassword !== snmpAgentData?.privPassword) {
      isValidTrap = false;
    }
    return isValidTrap;
  };

  const handlePassChange = (event: any) => {
    const { name, value } = event.target;
    const existing = store.getState().helpers.errorMsg;

    if (name === "authPassword") {
      setAuthPassword(value);

      if (value) {
        const isValidPass = isValidPassword(value);
        dispatch(setErrorMsg({
          ...existing,
          authPassword: isValidPass ? "" : "Password is invalid. It must meet the specified criteria!",
        }));
      } else {
        dispatch(setErrorMsg({ ...existing, authPassword: "" }));
      }

    } else if (name === "privPassword") {
      setPrivPassword(value);

      if (value) {
        const isValidPass = isValidPassword(value);
        dispatch(setErrorMsg({
          ...existing,
          privPassword: isValidPass ? "" : "Password is invalid. It must meet the specified criteria!",
        }));
      } else {
        dispatch(setErrorMsg({ ...existing, privPassword: "" }));
      }

    } else if (name === "authConfirmPassword") {
      setAuthConfirmPassword(value);
      dispatch(setErrorMsg({
        ...existing,
        authConfirmPassword: value !== authPassword ? "Passwords do not match" : "",
      }));

    } else if (name === "privConfirmPassword") {
      setPrivConfirmPassword(value);
      dispatch(setErrorMsg({
        ...existing,
        privConfirmPassword: value !== privPassword ? "Passwords do not match" : "",
      }));
    }
  };


  const handleProtocol = (event: any) => {
    const { name, checked } = event.target;
    const existing = store.getState().helpers.errorMsg;
    if (name === "auth") {
      setIsAuthChecked(checked);
      if (!checked) {
        // Clear auth password errors when unchecking
        dispatch(setErrorMsg({
          ...existing,
          authPassword: "",
          authConfirmPassword: ""
        }));
      }
    } else {
      setIsPrivChecked(checked);
      if (!checked) {
        // Clear priv password errors when unchecking
        dispatch(setErrorMsg({
          ...existing,
          privPassword: "",
          privConfirmPassword: ""
        }));
      }
    }
  };

  const selectWithLabelSxProps: SxProps = {
    width: "100%",
    mb: 4,
  };

  const [isEditMode, setIsEditMode] = useState(false);

  const authProtocolOptions = getProtocolList(AuthenticationOptions);
  const privacyProtocolOptions = getProtocolList(PrivacyOptions);

  const getProtocolDisplayValue = (value: string | undefined, options: Array<{ text: string; value: string }>) => {
    if (!value) return "-";
    return options.find((item) => item.value === value)?.text || value;
  };

  const handleEnterEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEditMode = () => {
    handleResetSnmpAgent();
    dispatch(setErrorMsg({}));
    setIsEditMode(false);
  };

  const handleSaveClick = async () => {
    const isSuccess = await submitHandler();
    if (isSuccess) {
      setIsEditMode(false);
    }
  };

  // const isSaveDisabled = 
  // isLoading || 
  // isSnmpAgentDataUpdated(snmpAgentDetails, snmpAgentData) || 
  // Object.values(errMsg).some((err) => err !== "") ||
  // !(enableV2 || enableV3); 

  return (
    <div
      className="flex flex-col w-full px-4 md:px-4 pt-1 pb-4"
      style={{ color: "var(--foreground)" }}
    >
      {openConfirmDialog === DIALOGOPENFOR.SNMPVERSION && (
        <ConfirmDialog actionName={constStr.ok} handleClickAction={handleSelectVersion}>
          {handleMsg()}
        </ConfirmDialog>
      )}

      <div className="rounded-md border border-border shadow-sm">
        <div className="flex w-full items-start justify-between border-b border-border px-6 py-2">
          <div className="mb-3 flex flex-col items-start text-left">
            <h2 className="m-0 mt-4 text-sm font-semibold leading-tight text-foreground">SNMP Agent</h2>
            <p className="m-0 mt-2 text-sm text-muted-foreground">Internal agent, community strings, and SNMP v3 parameters.</p>
          </div>
          <div className="mt-2 shrink-0">
            {!isEditMode ? (
              <Button type="button" onClick={handleEnterEditMode} className="bg-[#004f69] text-white hover:bg-[#004f69]/90">
                Edit
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={handleCancelEditMode}>
                  Cancel
                </Button>
                <Button type="button" disabled={isLoading} onClick={handleSaveClick} className="bg-[#004f69] text-white hover:bg-[#004f69]/90">
                  Save
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          <form autoComplete="off" className="space-y-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                <div className="flex flex-col gap-1">
                  {isEditMode ? (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="snmpAgentEnable"
                        checked={agentEnable}
                        onCheckedChange={(checked) => handleSnmpAgentEnable(!!checked)}
                        className="m-0"
                      />
                      <Label htmlFor="snmpAgentEnable" className="cursor-pointer">Enable Internal SNMP Agent</Label>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm text-muted-foreground">Enable Internal SNMP Agent</span>
                      <div className="text-sm font-semibold text-foreground">{agentEnable ? "Yes" : "No"}</div>
                    </>
                  )}
                </div>

                <div />

                <div className="flex flex-col gap-1">
                  {isEditMode ? (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="enableV2"
                        disabled={!agentEnable}
                        checked={enableV2}
                        onCheckedChange={(checked) => handleVersionCheckbox("enableV2", !!checked)}
                        className="m-0"
                      />
                      <Label htmlFor="enableV2" className="cursor-pointer">SNMP V2C</Label>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm text-muted-foreground">SNMP V2C</span>
                      <div className="text-sm font-semibold text-foreground">
                        {enableV2 ? "Yes" : "No"}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                <div className="flex flex-col gap-1">
                  {isEditMode ? (
                    <div className="w-4/5">
                    <InputField
                      name="readComunity"
                      label="Read Community"
                      disabled={!enableV2}
                      value={snmpAgentData?.readComunity || ""}
                      onChange={onFieldChange}
                      onBlur={onBlurHandler}
                      maxLength={255}
                      tooltipText="Maximum length: 255 characters"
                      placeholder="Enter read community"
                    />
                    </div>
                  ) : (
                    <>
                      <span className="text-sm text-muted-foreground">Read Community</span>
                      <div className="text-sm font-semibold text-foreground">{snmpAgentData?.readComunity || "-"}</div>
                    </>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  {isEditMode ? (
                    <div className="w-4/5">
                    <InputField
                      name="writeComunity"
                      label="Write Community"
                      disabled={!enableV2}
                      value={snmpAgentData?.writeComunity || ""}
                      onChange={onFieldChange}
                      onBlur={onBlurHandler}
                      maxLength={255}
                      tooltipText="Maximum length: 255 characters"
                      placeholder="Enter write community"
                    />
                    </div>
                  ) : (
                    <>
                      <span className="text-sm text-muted-foreground">Write Community</span>
                      <div className="text-sm font-semibold text-foreground">{snmpAgentData?.writeComunity || "-"}</div>
                    </>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  {isEditMode ? (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="enableV3"
                        disabled={!agentEnable}
                        checked={enableV3}
                        onCheckedChange={(checked) => handleVersionCheckbox("enableV3", !!checked)}
                        className="m-0"
                      />
                      <Label htmlFor="enableV3" className="cursor-pointer">SNMP V3</Label>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm text-muted-foreground">SNMP V3</span>
                      <div className="text-sm font-semibold text-foreground">
                        {enableV3 ? "Yes" : "No"}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {enableV3 && (
            <div className="rounded-md border border-border bg-muted dark:bg-[#222e40] p-3">
              {isEditMode ? (
              <div className="space-y-8">
                <div className="grid grid-cols-1 gap-x-16 gap-y-9 md:grid-cols-2">
                  <div className="flex flex-col gap-2 w-4/5">
                    <InputField
                      name="engineId"
                      label="SNMP Engine ID"
                      disabled={!enableV3}
                      value={snmpAgentData?.engineId || ""}
                      onChange={onFieldChange}
                      onBlur={onBlurHandler}
                      maxLength={255}
                      tooltipText="Maximum length: 255 characters"
                      placeholder="Enter engine id"
                    />
                  </div>

                  <div />

                  <div className="flex flex-col gap-2 w-2/4">
                    <InputField
                      name="userName"
                      label="User Name"
                      disabled={!enableV3}
                      value={snmpAgentData?.userName || ""}
                      onChange={onFieldChange}
                      onBlur={onBlurHandler}
                      maxLength={255}
                      tooltipText="Maximum length: 255 characters"
                      placeholder="Enter user name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-4 w-1/3">
                      <Label htmlFor="authProtocol" className="text-sm font-medium">Authentication Protocol</Label>
                      <RadixSelectControl
                        disabled={!enableV3}
                        value={snmpAgentData?.authProtocol || ""}
                        options={getProtocolList(AuthenticationOptions).map((item: any) => ({ value: item.value, label: item.text }))}
                        onValueChange={(value) => onFieldChange("authProtocol", value)}
                      />
                      {storeErrMsg?.authProtocol && <p className="m-0 text-xs text-destructive">{storeErrMsg.authProtocol}</p>}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="authProtocolCheckbox"
                      disabled={!enableV3}
                      checked={isAuthChecked}
                      onCheckedChange={(checked) => {
                        setIsAuthChecked(!!checked);
                        const existing = store.getState().helpers.errorMsg;
                        if (checked) {
                          dispatch(setErrorMsg({ ...existing, authProtocol: "" }));
                        } else {
                          dispatch(setErrorMsg({ ...existing, authPassword: "", authConfirmPassword: "" }));
                        }
                      }}
                      className="m-0"
                    />
                    <Label htmlFor="authProtocolCheckbox" className="cursor-pointer">Change Authentication Protocol Password</Label>
                  </div>
                </div>

                {isAuthChecked && (
                  <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <PasswordField
                        label="Authentication Password"
                        name="authPassword"
                        disabled={snmpAgentData?.authProtocol?.toLowerCase() === "no_auth"}
                        value={authPassword}
                        onChange={onPassChange}
                        onBlur={onBlurHandler}
                        placeholder="Enter password"
                      />
                    </div>

                    <div />

                    <div className="flex flex-col gap-2">
                      <PasswordField
                        label="Confirm Authentication Password"
                        name="authConfirmPassword"
                        disabled={snmpAgentData?.authProtocol?.toLowerCase() === "no_auth"}
                        value={authConfirmPassword}
                        onChange={onPassChange}
                        onBlur={onBlurHandler}
                        placeholder="Confirm password"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-4 w-1/3">
                      <Label htmlFor="privProtocol" className="text-sm font-medium">Privacy Protocol</Label>
                      <RadixSelectControl
                        disabled={!enableV3}
                        value={snmpAgentData?.privProtocol || ""}
                        options={getProtocolList(PrivacyOptions).map((item: any) => ({ value: item.value, label: item.text }))}
                        onValueChange={(value) => onFieldChange("privProtocol", value)}
                      />
                      {storeErrMsg?.privProtocol && <p className="m-0 text-xs text-destructive">{storeErrMsg.privProtocol}</p>}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="privacyProtocolCheckbox"
                      disabled={!enableV3}
                      checked={isPrivChecked}
                      onCheckedChange={(checked) => {
                        setIsPrivChecked(!!checked);
                        const existing = store.getState().helpers.errorMsg;
                        if (checked) {
                          dispatch(setErrorMsg({ ...existing, privProtocol: "" }));
                        } else {
                          dispatch(setErrorMsg({ ...existing, privPassword: "", privConfirmPassword: "" }));
                        }
                      }}
                      className="m-0"
                    />
                    <Label htmlFor="privacyProtocolCheckbox" className="cursor-pointer">Change Privacy Protocol Password</Label>
                  </div>
                </div>

                {isPrivChecked && (
                  <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <PasswordField
                        label="Privacy Password"
                        name="privPassword"
                        disabled={snmpAgentData?.privProtocol?.toLowerCase() === "no_priv"}
                        value={privPassword}
                        onChange={onPassChange}
                        onBlur={onBlurHandler}
                        placeholder="Enter password"
                      />
                    </div>

                    <div />

                    <div className="flex flex-col gap-2">
                      <PasswordField
                        label="Confirm Privacy Password"
                        name="privConfirmPassword"
                        disabled={snmpAgentData?.privProtocol?.toLowerCase() === "no_priv"}
                        value={privConfirmPassword}
                        onChange={onPassChange}
                        onBlur={onBlurHandler}
                        placeholder="Confirm password"
                      />
                    </div>
                  </div>
                )}
              </div>
              ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground">SNMP Engine ID</span>
                    <div className="text-sm font-semibold text-foreground">{snmpAgentData?.engineId || "-"}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground">User Name</span>
                    <div className="text-sm font-semibold text-foreground">{snmpAgentData?.userName || "-"}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground">Authentication Protocol</span>
                    <div className="text-sm font-semibold text-foreground">
                      {getProtocolDisplayValue(snmpAgentData?.authProtocol, authProtocolOptions)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground">Change Authentication Protocol Password</span>
                    <div className="text-sm font-semibold text-foreground">{isAuthChecked ? "Yes" : "No"}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground">Privacy Protocol</span>
                    <div className="text-sm font-semibold text-foreground">
                      {getProtocolDisplayValue(snmpAgentData?.privProtocol, privacyProtocolOptions)}
                    </div>
                  </div>
                </div>

                {isAuthChecked && (
                  <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-muted-foreground">Authentication Password</span>
                      <div className="text-sm font-semibold text-foreground">{authPassword ? "********" : "-"}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-muted-foreground">Confirm Authentication Password</span>
                      <div className="text-sm font-semibold text-foreground">{authConfirmPassword ? "********" : "-"}</div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground">Change Privacy Protocol Password</span>
                    <div className="text-sm font-semibold text-foreground">{isPrivChecked ? "Yes" : "No"}</div>
                  </div>
                </div>

                {isPrivChecked && (
                  <div className="grid grid-cols-1 gap-x-16 gap-y-6 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-muted-foreground">Privacy Password</span>
                      <div className="text-sm font-semibold text-foreground">{privPassword ? "********" : "-"}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-muted-foreground">Confirm Privacy Password</span>
                      <div className="text-sm font-semibold text-foreground">{privConfirmPassword ? "********" : "-"}</div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
            )}
          </form>
        </div>
      </div>

    </div>
  );
};

export default SnmpAgent;
